// src/inviteToken.ts
var INVITE_PATH = /^\/invite\/([0-9a-f]{64})\/?$/;
var TOKEN = /^[0-9a-f]{64}$/;
var STASH_KEY = "pending-invite-token";
function inviteTokenFromPath(pathname) {
  return INVITE_PATH.exec(pathname)?.[1] ?? null;
}
function stashInviteToken(token) {
  if (!TOKEN.test(token)) throw new Error(`stashInviteToken: not an invite token: ${token}`);
  try {
    sessionStorage.setItem(STASH_KEY, token);
  } catch {
    return false;
  }
  return true;
}
function takeStashedInviteToken() {
  let stored;
  try {
    stored = sessionStorage.getItem(STASH_KEY);
    sessionStorage.removeItem(STASH_KEY);
  } catch {
    return { available: false };
  }
  return { available: true, token: stored !== null && TOKEN.test(stored) ? stored : null };
}

// src/SharingError.ts
var SharingError = class extends Error {
  status;
  // null when the response carried no code, e.g. the identity provider's 401.
  code;
  constructor(message, status, code) {
    super(message);
    this.name = "SharingError";
    this.status = status;
    this.code = code;
  }
};

// src/useGroupRole.ts
import { useCallback as useCallback2, useEffect, useRef as useRef2, useState as useState2 } from "react";

// src/useSharing.ts
import { useCallback, useRef, useState } from "react";
var DEFAULT_BASE_URL = "/api/shares";
var DEFAULT_TIMEOUT_MS = 3e4;
async function sharingErrorOf(res, method, path) {
  const failed = `${method} ${path} failed with ${res.status}`;
  if (!/^application\/json\b/i.test(res.headers.get("Content-Type") ?? "")) {
    return new SharingError(failed, res.status, null);
  }
  const text = await res.text();
  let data;
  try {
    data = JSON.parse(text);
  } catch {
    return new SharingError(`${failed} (unparsable JSON body)`, res.status, null);
  }
  const body = typeof data === "object" && data !== null ? data : {};
  const message = body.error === void 0 ? failed : String(body.error);
  const code = typeof body.code === "string" ? body.code : null;
  return new SharingError(message, res.status, code);
}
function useSharing(options = {}) {
  const baseUrl = options.apiBaseUrl ?? DEFAULT_BASE_URL;
  const fetchFn = options.fetchFn ?? fetch;
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  if (!Number.isInteger(timeoutMs) || timeoutMs < 1) {
    throw new Error(`useSharing: timeoutMs must be a positive integer, got ${timeoutMs}`);
  }
  const [inFlight, setInFlight] = useState(0);
  const [error, setError] = useState(null);
  const started = useRef(0);
  const errorFrom = useRef(0);
  const call = useCallback(
    async (path, method, body) => {
      const seq = ++started.current;
      setInFlight((n) => n + 1);
      const controller = new AbortController();
      let timer;
      const timeout = new Promise((_resolve, reject) => {
        timer = setTimeout(() => {
          controller.abort();
          reject(new Error(`${method} ${path} timed out after ${timeoutMs} ms`));
        }, timeoutMs);
      });
      const exchange = async () => {
        const res = await fetchFn(`${baseUrl}${path}`, {
          method,
          credentials: "include",
          signal: controller.signal,
          headers: { "Content-Type": "application/json" },
          ...body === void 0 ? {} : { body: JSON.stringify(body) }
        });
        if (!res.ok) throw await sharingErrorOf(res, method, path);
        return res.json();
      };
      try {
        const data = await Promise.race([exchange(), timeout]);
        if (seq > errorFrom.current) setError(null);
        return data;
      } catch (err) {
        const asError3 = err instanceof Error ? err : new Error(String(err));
        errorFrom.current = seq;
        setError(asError3);
        throw asError3;
      } finally {
        clearTimeout(timer);
        setInFlight((n) => n - 1);
      }
    },
    [baseUrl, fetchFn, timeoutMs]
  );
  const createInvite = useCallback(
    (targetGroupId, recipientEmail, role, extra) => call("/invite", "POST", {
      targetGroupId,
      recipientEmail,
      role,
      ...extra?.appRole === void 0 ? {} : { appRole: extra.appRole },
      ...extra?.sendEmail === void 0 ? {} : { sendEmail: extra.sendEmail },
      ...extra?.expiresInDays === void 0 ? {} : { expiresInDays: extra.expiresInDays },
      ...extra?.targetName === void 0 ? {} : { targetName: extra.targetName }
    }),
    [call]
  );
  const validateInvite = useCallback(
    (token) => call(`/validate/${encodeURIComponent(token)}`, "GET"),
    [call]
  );
  const acceptInvite = useCallback(
    (token) => call("/accept", "POST", { token }),
    [call]
  );
  const getPendingInvites = useCallback(
    async (targetGroupId) => {
      const result = await call(
        `/targets/${encodeURIComponent(targetGroupId)}/invites`,
        "GET"
      );
      return result.invites;
    },
    [call]
  );
  const revokeInvite = useCallback(
    async (token) => {
      await call(`/invites/${encodeURIComponent(token)}`, "DELETE");
    },
    [call]
  );
  const getCollaborators = useCallback(
    async (targetGroupId) => {
      const result = await call(
        `/targets/${encodeURIComponent(targetGroupId)}/collaborators`,
        "GET"
      );
      return result.collaborators;
    },
    [call]
  );
  const removeCollaborator = useCallback(
    async (targetGroupId, accountId) => {
      await call(
        `/targets/${encodeURIComponent(targetGroupId)}/collaborators/${encodeURIComponent(accountId)}`,
        "DELETE"
      );
    },
    [call]
  );
  const getUserMemberships = useCallback(async () => {
    const result = await call("/user/memberships", "GET");
    return result.memberships;
  }, [call]);
  const getGroupRole = useCallback(
    async (targetGroupId) => {
      const result = await call(
        `/targets/${encodeURIComponent(targetGroupId)}/role`,
        "GET"
      );
      if (result.role !== null && typeof result.role !== "string" || typeof result.isAdmin !== "boolean") {
        throw new Error(
          `GET /targets/${targetGroupId}/role: reply needs a role (string or null) and a boolean isAdmin: ${JSON.stringify(result)}`
        );
      }
      return { role: result.role, isAdmin: result.isAdmin };
    },
    [call]
  );
  return {
    createInvite,
    validateInvite,
    acceptInvite,
    getPendingInvites,
    revokeInvite,
    getCollaborators,
    removeCollaborator,
    getUserMemberships,
    getGroupRole,
    isLoading: inFlight > 0,
    error
  };
}

// src/useGroupRole.ts
function useGroupRole(groupId, sharing) {
  const own = useSharing();
  const apiRef = useRef2(sharing ?? own);
  apiRef.current = sharing ?? own;
  const [settled, setSettled] = useState2(null);
  const loadSeq = useRef2(0);
  const busy = useRef2(false);
  const load = useCallback2((target) => {
    const seq = ++loadSeq.current;
    busy.current = true;
    const settle = (state) => {
      if (seq !== loadSeq.current) return;
      busy.current = false;
      setSettled({ groupId: target, state });
    };
    apiRef.current.getUserMemberships().then(
      (memberships) => {
        const role = memberships.find((m) => m.groupId === target)?.role ?? null;
        settle({ status: "ok", role });
      },
      (err) => {
        settle({ status: "error", error: err instanceof Error ? err : new Error(String(err)) });
      }
    );
  }, []);
  useEffect(() => {
    if (!groupId) return;
    load(groupId);
    const again = () => {
      if (!busy.current) load(groupId);
    };
    window.addEventListener("focus", again);
    window.addEventListener("online", again);
    return () => {
      loadSeq.current++;
      busy.current = false;
      setSettled(null);
      window.removeEventListener("focus", again);
      window.removeEventListener("online", again);
    };
  }, [groupId, load]);
  if (!groupId) return { status: "idle" };
  if (settled === null || settled.groupId !== groupId) return { status: "loading" };
  return settled.state;
}

// src/useInviteAcceptance.ts
import { useCallback as useCallback3, useEffect as useEffect2, useRef as useRef3, useState as useState3 } from "react";
function asError(err) {
  return err instanceof Error ? err : new Error(String(err));
}
function codeOf(err) {
  return err instanceof SharingError ? err.code : null;
}
function validState(data) {
  const raw = data;
  const inviterOk = raw.inviter?.deleted === false && typeof raw.inviter.email === "string";
  const targetNameOk = raw.targetName === null || typeof raw.targetName === "string";
  if (!inviterOk || typeof raw.role !== "string" || !targetNameOk) {
    throw new Error(`validate answered a malformed valid invite: ${JSON.stringify(data)}`);
  }
  return { status: "valid", inviter: data.inviter, role: data.role, targetName: data.targetName };
}
function useInviteAcceptance({
  token,
  isAuthenticated,
  sessionPending,
  sharing
}) {
  const own = useSharing();
  const apiRef = useRef3(sharing ?? own);
  apiRef.current = sharing ?? own;
  const [state, setState] = useState3({ status: "loading" });
  const tokenRef = useRef3(token);
  tokenRef.current = token;
  const validatedFor = useRef3(null);
  const acceptLock = useRef3(null);
  const validateSeq = useRef3(0);
  useEffect2(() => {
    if (acceptLock.current === token) return;
    if (sessionPending) {
      if (validatedFor.current !== token) setState({ status: "loading" });
      return;
    }
    if (!isAuthenticated) {
      validatedFor.current = null;
      validateSeq.current++;
      setState({ status: "not_authenticated" });
      return;
    }
    if (validatedFor.current === token) return;
    validatedFor.current = token;
    const seq = ++validateSeq.current;
    setState({ status: "loading" });
    apiRef.current.validateInvite(token).then(
      (data) => {
        if (seq !== validateSeq.current) return;
        if (data.valid) {
          try {
            setState(validState(data));
          } catch (err) {
            setState({ status: "error", reason: "failed", error: asError(err) });
          }
        } else if (data.code === "wrong_account") {
          setState({ status: "wrong_account" });
        } else if (data.code === "invalid_token") {
          setState({ status: "error", reason: "invalid", error: null });
        } else {
          const unknown = new Error(`validate answered an unknown code: ${String(data.code)}`);
          setState({ status: "error", reason: "failed", error: unknown });
        }
      },
      (err) => {
        if (seq !== validateSeq.current) return;
        setState({ status: "error", reason: "failed", error: asError(err) });
      }
    );
  }, [token, isAuthenticated, sessionPending]);
  const ready = state.status === "valid";
  const accept = useCallback3(async () => {
    if (!ready || acceptLock.current === token) return;
    acceptLock.current = token;
    validateSeq.current++;
    setState({ status: "accepting" });
    try {
      const result = await apiRef.current.acceptInvite(token);
      if (tokenRef.current !== token) return;
      setState({
        status: "accepted",
        targetGroupId: result.targetGroupId,
        alreadyMember: result.alreadyMember
      });
    } catch (err) {
      if (tokenRef.current !== token) return;
      acceptLock.current = null;
      const code = codeOf(err);
      if (code === "wrong_account") setState({ status: "wrong_account" });
      else if (code === "invalid_token")
        setState({ status: "error", reason: "invalid", error: asError(err) });
      else if (code === "undo_pending")
        setState({ status: "error", reason: "undo_pending", error: asError(err) });
      else setState({ status: "error", reason: "failed", error: asError(err) });
    }
  }, [ready, token]);
  return { state, accept };
}

// src/useShareManager.ts
import { useCallback as useCallback4, useEffect as useEffect3, useRef as useRef4, useState as useState4 } from "react";
function asError2(err) {
  return err instanceof Error ? err : new Error(String(err));
}
function useShareManager(groupId, sharing) {
  const own = useSharing();
  const api = sharing ?? own;
  const apiRef = useRef4(api);
  apiRef.current = api;
  const [collaborators, setCollaborators] = useState4([]);
  const [pendingInvites, setPendingInvites] = useState4([]);
  const [loading, setLoading] = useState4(groupId !== null);
  const [error, setError] = useState4(null);
  const [lastShareUrl, setLastShareUrl] = useState4(null);
  const groupRef = useRef4(groupId);
  groupRef.current = groupId;
  const loadSeq = useRef4(0);
  const load = useCallback4(async (target) => {
    const seq = ++loadSeq.current;
    if (target === null) {
      setCollaborators([]);
      setPendingInvites([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [collabs, access] = await Promise.all([
        apiRef.current.getCollaborators(target),
        apiRef.current.getGroupRole(target)
      ]);
      const invites = access.isAdmin ? await apiRef.current.getPendingInvites(target) : [];
      if (seq !== loadSeq.current) return;
      setCollaborators(collabs);
      setPendingInvites(invites);
    } catch (err) {
      if (seq !== loadSeq.current) return;
      setError(asError2(err));
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, []);
  useEffect3(() => {
    setCollaborators([]);
    setPendingInvites([]);
    setError(null);
    setLastShareUrl(null);
    void load(groupId);
    return () => {
      loadSeq.current++;
    };
  }, [groupId, load]);
  const reload = useCallback4(async () => {
    setError(null);
    await load(groupRef.current);
  }, [load]);
  const mutate = useCallback4(
    async (op) => {
      const target = groupRef.current;
      if (target === null) return null;
      setError(null);
      try {
        return await op(target);
      } catch (err) {
        if (groupRef.current === target) setError(asError2(err));
        return null;
      } finally {
        if (groupRef.current === target) await load(target);
      }
    },
    [load]
  );
  const invite = useCallback4(
    (email, role, options = {}) => mutate(async (target) => {
      const result = await apiRef.current.createInvite(target, email.trim(), role, options);
      if (groupRef.current === target) setLastShareUrl(result.shareUrl);
      return result;
    }),
    [mutate]
  );
  const remove = useCallback4(
    async (accountId) => await mutate(async (target) => {
      await apiRef.current.removeCollaborator(target, accountId);
      return true;
    }) === true,
    [mutate]
  );
  const revoke = useCallback4(
    async (token) => await mutate(async () => {
      await apiRef.current.revokeInvite(token);
      return true;
    }) === true,
    [mutate]
  );
  return {
    collaborators,
    pendingInvites,
    loading,
    error,
    lastShareUrl,
    invite,
    remove,
    revoke,
    reload
  };
}
export {
  SharingError,
  inviteTokenFromPath,
  stashInviteToken,
  takeStashedInviteToken,
  useGroupRole,
  useInviteAcceptance,
  useShareManager,
  useSharing
};
